import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { Field } from 'react-final-form';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { Alert } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { FormulaErrorInterface } from '@/formula/formula.types';

export interface FormulaEditorIF extends ValidatingFormComponent<string> {
  component: 'FormulaEditorFormComponent';
  formula: string;
  constants: any[];
  operators: any[];
  showLineNumbers: boolean;
  exposeFormulaToParent: (formula: string) => void;
  parameters: FormulaEditorParam[];
  onSave: (formula: string) => any;
  formulaErrors: FormulaErrorInterface[];
  generalFormulaError: string;
  setFormulaErrors?: (errors: any) => void;
  readOnly?: boolean;
}

/**
 * A wrapper around the `FormulaEditor` component for use with `FormBuilder`
 */
export const FormulaEditorFormComponent = ({
  name,
  testId = 'formulaEditor',
  formula,
  constants,
  operators,
  showLineNumbers,
  exposeFormulaToParent,
  parameters,
  onSave,
  formulaErrors,
  generalFormulaError,
  setFormulaErrors,
  readOnly,
  extendValidation,
  validation,
  xstyle,
  wrapperXstyle,
}: FormulaEditorIF) => {
  const defaultValidation = () => {
    return !_.isEmpty(formulaErrors);
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  return (
    <div {...stylex.props(styles.editorField, wrapperXstyle)} data-testid={testId}>
      <Field name={name} validate={appliedValidation}>
        {({ input }) => (
          <FormulaEditorAsync
            xstyle={xstyle}
            formula={formula}
            constants={constants}
            operators={operators}
            showLineNumbers={showLineNumbers}
            exposeFormulaToParent={(formulaValue) => {
              if (formulaValue !== formula) {
                exposeFormulaToParent(formulaValue);
                input.onChange(formulaValue);
              }
            }}
            parameters={parameters}
            onSave={onSave}
            formulaErrors={formulaErrors}
            setFormulaErrors={setFormulaErrors}
            readOnly={readOnly}
          />
        )}
      </Field>
      {generalFormulaError && (
        <Alert variant="formulaError" xstyle={styles.formulaError}>
          <ContainerWithHTML content={generalFormulaError} />
        </Alert>
      )}
    </div>
  );
};

const styles = stylex.create({
  editorField: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    marginBottom: '.5rem',
  },
  formulaError: {
    display: 'flex',
    flexDirection: 'row',
    padding: '.3125rem',
    fontFamily: 'var(--font-family-sans-serif)',
    fontSize: '.8125rem',
  },
});
